import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '../../lib/errors'
import { formatDateTime } from '../../lib/format'
import { listBroadcasts, type BroadcastSummary } from './api'
import { countsText, modeText } from './describe'

/**
 * A posting's broadcasts, newest first — on the posting's own page, where the dispatcher
 * starts the next one. Each opens on its own page, with every recipient's copy.
 */
export function BroadcastsPanel({ postingId }: { postingId: string }) {
  const { t, i18n } = useTranslation()
  const [broadcasts, setBroadcasts] = useState<BroadcastSummary[] | null>(null)
  const [failure, setFailure] = useState<{ error: unknown } | null>(null)

  useEffect(() => {
    let active = true
    setBroadcasts(null)
    setFailure(null)

    listBroadcasts(postingId)
      .then((found) => {
        if (active) {
          setBroadcasts(found)
        }
      })
      .catch((caught: unknown) => {
        if (active) {
          setFailure({ error: caught })
        }
      })

    return () => {
      active = false
    }
  }, [postingId])

  return (
    <section className="broadcast-section" aria-labelledby="posting-broadcasts">
      <h2 id="posting-broadcasts">{t('broadcasts.panel.title')}</h2>
      {failure !== null ? (
        <p className="alert" role="alert">
          {apiErrorMessage(failure.error, t, 'broadcasts.loadError')}
        </p>
      ) : broadcasts === null ? (
        <p className="muted">{t('broadcasts.loading')}</p>
      ) : broadcasts.length === 0 ? (
        <p className="placeholder">{t('broadcasts.panel.none')}</p>
      ) : (
        <ul className="broadcast-list">
          {broadcasts.map((broadcast) => (
            <li key={broadcast.id}>
              <Link to={`/availability/${postingId}/broadcasts/${broadcast.id}`}>
                {formatDateTime(broadcast.createdAt, i18n.language)}
              </Link>{' '}
              · {modeText(broadcast.mode, t)} · {t('broadcasts.panel.recipients', { count: broadcast.total })}
              {countsText(broadcast.counts, t) !== '' && <> · {countsText(broadcast.counts, t)}</>}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
